import React, { useState, useEffect, useContext } from "react";
import "./styles/EditRelevanceModal.scss";
import BoxFooter from "../../../../components/BoxFooter/BoxFooter";
import Button from "../../../../components/Button/Button";
import { toastSuccess, toastError } from "../../../../utils";
import { getService, putService } from "../../../../services/axios/services";
import AppContext from "../../../../context/appContext";
import PlotAndThematicAndUniverse from "../RelevanceTypes/PlotAndThematicAndUniverse";
import { Box } from "@mui/material";
import { emitProjectSheetInvalidation } from "../../../../shared/utils/projectSheetInvalidation";
import { RecommendationsAPIContext } from "../../../../context/RecommendationsAPI/recommendationsAPIContext";
import { HOTJAR_FEEDBACKS } from "../../../../services/Hotjar/constants";
import { useHotjar } from "../../../../context/HotjarContext";

const ORIGIN_MAP = {
  plot: 3,
  thematic: 6,
  universe: 9,
};

const LABEL_MAP = {
  plot: "Trama",
  thematic: "Temática",
  universe: "Universo",
};

const DESCRIPTION_MAP = {
  plot: "as tramas",
  thematic: "as temáticas",
  universe: "os universos",
};

const PLOT_RELEVANCE_MAP = {
  primaries: 1,
  secundaries: 2,
  tertiary: 3,
};

const EditRelevanceModal = ({
  type,
  id,
  data,
  discardText,
  onDiscard,
  isProcessingByAI = false,
  setCloseModal,
  workflowNoContent = false,
}) => {
  const appContext = useContext(AppContext);
  const { resetWorkflowState } = useContext(RecommendationsAPIContext);
  const { setRunHotjarSurvey } = useHotjar();

  const [tab, setTab] = useState("primaries");
  const [options, setOptions] = useState({});
  const [hasError, setHasError] = useState(false);
  const [state, setState] = useState({
    primaries: [],
    secundaries: [],
    tertiary: [],
  });

  const compileLabel = (data) => {
    return (
      data?.map((item) => ({
        id: item.generatedByAI ? 0 : item.id,
        thesaurusId: item.thesaurusId || item.id || null,
        label: item.name || item.label,
        descriptionContext: item.descriptionContext || "",
        generatedByAI: item.generatedByAI || false,
      })) || []
    );
  };

  const getThesaurus = async (origin) => {
    const res = await getService({
      endpoint: `dramaturgies/thesaurus?origins=${origin}`,
    });
    return res.data.map((item) => ({
      value: item.id,
      label: item.name,
      ...item,
    }));
  };

  useEffect(() => {
    if (!type || !ORIGIN_MAP[type] || hasError) return;

    getThesaurus(ORIGIN_MAP[type])
      .then((thesaurus) => {
        setOptions(thesaurus);

        const initial = data?.[type];
        setState({
          primaries: compileLabel(initial?.primaries),
          secundaries: compileLabel(initial?.secundaries),
          tertiary: compileLabel(initial?.tertiaries),
        });
      })
      .catch((error) => {
        console.error("Erro ao carregar dados do thesaurus:", error);
        setHasError(true);
        toastError("Erro ao carregar dados");
      });
  }, [type]);

  const handleAddItem = (selected, key, relevanceKey) => {
    if (!selected || selected.length === 0) return;

    const item = selected[selected.length - 1];
    const newItem = {
      id: 0,
      thesaurusId: item.value,
      label: item.label,
      description: "",
    };

    setState((prev) => ({
      ...prev,
      [relevanceKey]: [...prev[relevanceKey], newItem],
    }));
  };

  const handleUpdateItem = (updatedItem, index, relevanceKey) => {
    const newItem = {
      id: updatedItem.id || 0,
      thesaurusId:
        updatedItem.thesaurusId || updatedItem.value || updatedItem.id,
      label: updatedItem.label || updatedItem.name,
      descriptionContext: updatedItem.descriptionContext || "",
    };

    setState((prev) => {
      const updatedList = [...prev[relevanceKey]];
      updatedList[index] = {
        ...updatedList[index],
        ...newItem,
      };
      return {
        ...prev,
        [relevanceKey]: updatedList,
      };
    });
  };

  const handleDescriptionChange = (relevanceKey, index, value) => {
    setState((prev) => {
      const updated = [...prev[relevanceKey]];
      updated[index] = {
        ...updated[index],
        descriptionContext: value,
      };
      return { ...prev, [relevanceKey]: updated };
    });
  };

  const getRelevanceNumber = (key) => {
    if (key === "primaries") return 1;
    if (key === "secundaries") return 2;
    if (key === "tertiary") return 3;
  };

  const sendData = (e) => {
    e.preventDefault();

    appContext.setShowGlassLoading(true);

    const isPlot = type === "plot";

    let payload;
    let endpoint;

    if (isPlot) {
      endpoint = `plots/contents/${id}`;

      const formatPlotPayload = (items, relevanceKey) =>
        items.map((item) => ({
          id: item.id,
          thesaurusId: item.thesaurusId,
          description: item.descriptionContext || "",
          relevance: PLOT_RELEVANCE_MAP[relevanceKey],
        }));

      payload = [
        ...formatPlotPayload(state.primaries, "primaries"),
        ...formatPlotPayload(state.secundaries, "secundaries"),
        ...formatPlotPayload(state.tertiary, "tertiary"),
      ];
    } else {
      endpoint = `dramaturgies/${type}`;

      const formatRelevance = (items, relevanceKey) =>
        items.map((item) => ({
          id: item.thesaurusId,
          complementContext: item.descriptionContext,
          relevance: getRelevanceNumber(relevanceKey),
        }));

      payload = {
        relevantComplementaryDataItems: [
          ...formatRelevance(state.primaries, "primaries"),
          ...formatRelevance(state.secundaries, "secundaries"),
          ...formatRelevance(state.tertiary, "tertiary"),
        ],
        contentId: id,
      };
    }

    putService({ endpoint, putData: payload })
      .then(() => {
        appContext.setShowGlassLoading(false);
        resetWorkflowState();
        emitProjectSheetInvalidation();
        toastSuccess("Ficha atualizada com sucesso!");
        if (isProcessingByAI) {
          setRunHotjarSurvey({
            surveyName: HOTJAR_FEEDBACKS.FEEDBACK_PLOT_RECOMMENDATION,
          });
        }
        appContext.setCurrentStepBox("closed");
        if (typeof setCloseModal === "function") {
          setCloseModal();
        }
      })
      .catch(() => {
        appContext.setShowGlassLoading(false);
        toastError("Erro ao atualizar a ficha!");
      });
  };

  const handleRemoveItem = (relevanceKey, index) => {
    setState((prev) => {
      const updated = [...prev[relevanceKey]];
      updated.splice(index, 1);
      return { ...prev, [relevanceKey]: updated };
    });
  };

  return (
    <div className="editRelevanceModal">
      <div className="relevanceNavigation">
        <div
          className={`tab ${tab === "primaries" ? "active" : ""}`}
          onClick={() => setTab("primaries")}
        >
          Central ({state.primaries.length})
        </div>
        <div
          className={`tab ${tab === "secundaries" ? "active" : ""}`}
          onClick={() => setTab("secundaries")}
        >
          Secundária ({state.secundaries.length})
        </div>
        <div
          className={`tab ${tab === "tertiary" ? "active" : ""}`}
          onClick={() => setTab("tertiary")}
        >
          Terciária ({state.tertiary.length})
        </div>
      </div>

      <form onSubmit={sendData} className="relevanceForm">
        <p className="relevanceTip">
          Classifique {DESCRIPTION_MAP[type]} por relevância principal,
          secundária ou terciária.
        </p>

        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "row",
            gap: "24px",
            alignItems: "start",
          }}
        >
          <PlotAndThematicAndUniverse
            options={options}
            defaultValuePrimaries={tab === "primaries" ? state.primaries : []}
            defaultValueSecundaries={
              tab === "secundaries" ? state.secundaries : []
            }
            defaultValueTertiary={tab === "tertiary" ? state.tertiary : []}
            handleAddItem={handleAddItem}
            handleUpdateItem={handleUpdateItem}
            handleRemoveItem={handleRemoveItem}
            thesaurusName={LABEL_MAP[type]}
            keyName={type}
            origin={ORIGIN_MAP[type]}
            activeTab={tab}
            handleDescriptionChange={handleDescriptionChange}
            data={data}
            isProcessingByAI={isProcessingByAI}
            workflowNoContent={workflowNoContent}
          />
        </Box>

        <BoxFooter>
          {onDiscard && (
            <Button
              text={discardText}
              classButton="buttonCustomCancel large"
              typeButton="button"
              clickEvent={onDiscard}
              dataQA="button-edit-relevance-discard"
            />
          )}

          <Button
            text="Salvar"
            toValidate={true}
            isValidForm={!workflowNoContent}
            classButton="buttonGreen large"
            typeButton="submit"
            dataQA="button-edit-relevance-save"
          />
        </BoxFooter>
      </form>
    </div>
  );
};

export default EditRelevanceModal;
